import { toggleInArray, removeFromArray } from "@goodfill/array";
import { AccordionSection, Flex, InputCheckbox, Label } from "@goodfill/ui-components";
import { useState } from "react";
import {
  getSessionStorageArray,
  removeItemFromSessionStorage,
  saveArrayToSessionStorage,
} from "../../../../../sessionStorage";
import { PricedBond } from "../../../PricedBond";
import { BondFilter, BondFilterChipData, BondFilterData, BondFilterProps } from "../BondFilter";
import { BondFilterSectionGap, BondFilterSectionMargin } from "../BondFilterConst";
import { BondFilterCriteria } from "../BondFilterCriteria";
import { BondFilterDataConstraints } from "../BondFilterDataConstraints";
import { arrayDeepEqual } from "@goodfill/array";

const FILTER_INDUSTRIES_STORAGE_KEY = "filter:selectedIndustries";

const Component = (props: BondFilterProps) => {
  const selectedIndustries = props.criteria.selectedIndustries;
  const toggleIndustry = (value: string) => {
    const newlySelectedIndustries = toggleInArray(value, props.criteria.selectedIndustries);

    props.onChange({
      criteria: {
        ...props.criteria,
        selectedIndustries: newlySelectedIndustries,
      },
    });
  };

  const [industrySectionIsOpen, setIndustrySectionIsOpen] = useState(false);

  return (
    <AccordionSection title={"Industry"} isOpen={industrySectionIsOpen} setIsOpen={setIndustrySectionIsOpen}>
      <Flex direction="col" verticalMargin={BondFilterSectionMargin} gap={BondFilterSectionGap}>
        {Array.from(props.constraints.availableIndustries).map((industry) => {
          const checked = selectedIndustries.includes(industry);

          return (
            <Flex key={industry} direction="row" items="center" gap={3}>
              <InputCheckbox id={industry} checked={checked} onChange={() => toggleIndustry(industry)} />
              <Label uppercase={true} htmlFor={industry} accent={!checked} primary={checked}>
                {industry}
              </Label>
            </Flex>
          );
        })}
      </Flex>
    </AccordionSection>
  );
};

export class BondFilterIndustry implements BondFilter {
  isVisibleInDropdown(): boolean {
    return true;
  }

  reset(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterCriteria {
    return {
      ...criteria,
      selectedIndustries: defaultCriteria.selectedIndustries,
    };
  }

  getChips(criteria: BondFilterCriteria, _defaultCriteria: BondFilterCriteria): BondFilterChipData[] {
    return criteria.selectedIndustries.map(
      (industry): BondFilterChipData => ({
        id: industry,
        label: industry,
        onClick: () => ({ ...criteria, selectedIndustries: removeFromArray(criteria.selectedIndustries, industry) }),
      }),
    );
  }

  evaluate(data: BondFilterData, criteria: BondFilterCriteria): boolean {
    return criteria.selectedIndustries.length === 0 || criteria.selectedIndustries.includes(data.pricedBond.industry);
  }

  updateConstraintByPricedBond(
    _pricedBond: PricedBond,
    current: BondFilterDataConstraints,
  ): BondFilterDataConstraints {
    current.availableIndustries.add(_pricedBond.industry);

    return current;
  }

  afterCriteriaCalculation(current: BondFilterDataConstraints): BondFilterDataConstraints {
    return current;
  }

  getDefaultCriteriaConstraints(_pricedBond: PricedBond): Partial<BondFilterDataConstraints> {
    return {
      availableIndustries: new Set(),
    };
  }

  component = Component;

  applyCriteriaFromStorage(criteria: Partial<BondFilterCriteria>): Partial<BondFilterCriteria> {
    return {
      ...criteria,
      selectedIndustries: getSessionStorageArray(FILTER_INDUSTRIES_STORAGE_KEY),
    };
  }

  storeCriteriaToStorage(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): void {
    if (arrayDeepEqual(criteria.selectedIndustries.sort(), defaultCriteria.selectedIndustries.sort())) {
      removeItemFromSessionStorage(FILTER_INDUSTRIES_STORAGE_KEY);
    } else {
      saveArrayToSessionStorage(FILTER_INDUSTRIES_STORAGE_KEY, criteria.selectedIndustries);
    }
  }
}
